import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { Card } from '@/components/layout';
import styles from './styles.module.scss';

type LeagueSyncStatus = 'queued' | 'running' | 'completed' | 'failed';

interface LeagueSyncState {
  leagueId: number;
  leagueName: string;
  season: string | null;
  status: LeagueSyncStatus;
  queuedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  updated: number | null;
  skipped: number | null;
  error: string | null;
}

interface LeagueSyncStatusResponse {
  currentLeagueId: number | null;
  queuedLeagueIds: number[];
  leagues: LeagueSyncState[];
  timestamp: string;
}

interface SyncableLeague {
  id: number;
  name: string;
  season?: string | null;
  country?: string | null;
}

interface LeagueFullSyncPanelProps {
  leagues: SyncableLeague[];
  enabled: boolean;
}

const ACTIVE_STATUSES: LeagueSyncStatus[] = ['queued', 'running'];
const POLL_INTERVAL_MS = 3000;

const formatTime = (iso: string | null, locale: string): string =>
  iso ? new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' }) : '';

export const LeagueFullSyncPanel: React.FC<LeagueFullSyncPanelProps> = ({ leagues, enabled }) => {
  const { t, i18n } = useTranslation(['admin']);
  const queryClient = useQueryClient();

  const { data: syncStatus } = useQuery<LeagueSyncStatusResponse>({
    queryKey: ['admin-league-full-sync-status'],
    queryFn: async () => (await apiClient.getLeagueFullSyncStatus()).data.data,
    enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && data.leagues.some((l) => ACTIVE_STATUSES.includes(l.status)) ? POLL_INTERVAL_MS : false;
    },
  });

  const startMutation = useMutation({
    mutationFn: (leagueId: number) => apiClient.startLeagueFullSync(leagueId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-league-full-sync-status'] });
    },
  });

  const stateByLeague = new Map<number, LeagueSyncState>(
    (syncStatus?.leagues ?? []).map((state) => [state.leagueId, state])
  );

  const renderStatusTag = (state: LeagueSyncState | undefined) => {
    if (!state) {
      return <span className={styles.never}>{t('admin:leagueFullSyncNever')}</span>;
    }
    const label = t(`admin:leagueFullSync${state.status.charAt(0).toUpperCase()}${state.status.slice(1)}`);
    return (
      <span className={`${styles.tag} ${styles[state.status]}`}>
        {ACTIVE_STATUSES.includes(state.status) && <span className="spinner spinner--sm" />}
        {label}
      </span>
    );
  };

  const renderDetails = (state: LeagueSyncState | undefined) => {
    if (!state) return null;
    const locale = i18n.language;

    if (state.status === 'running') {
      return <span className={styles.details}>{t('admin:leagueFullSyncStartedAt', { time: formatTime(state.startedAt, locale) })}</span>;
    }
    if (state.status === 'queued') {
      return null;
    }
    return (
      <span className={styles.details}>
        {t('admin:leagueFullSyncFinishedAt', { time: formatTime(state.finishedAt, locale) })}
        {state.status === 'completed' && (
          <> · {t('admin:leagueFullSyncUpdated', { updated: state.updated ?? 0, skipped: state.skipped ?? 0 })}</>
        )}
        {state.status === 'failed' && state.error && <> · {state.error}</>}
      </span>
    );
  };

  const startErrorMessage = startMutation.isError
    ? ((startMutation.error as any)?.response?.data?.message
      || (startMutation.error as any)?.response?.data?.error
      || t('admin:leagueFullSyncStartError'))
    : null;

  return (
    <Card className="ls-mb-4">
      <h2 className="ls-h4 ls-mb-2">{t('admin:leagueFullSyncTitle')}</h2>
      <p className={styles.hint}>{t('admin:leagueFullSyncHint')}</p>

      {startErrorMessage && <div className={styles.error}>{startErrorMessage}</div>}

      <ul className={styles.list}>
        {leagues.map((league) => {
          const state = stateByLeague.get(league.id);
          const isActive = state ? ACTIVE_STATUSES.includes(state.status) : false;
          const isStarting = startMutation.isPending && startMutation.variables === league.id;

          return (
            <li key={league.id} className={styles.row}>
              <div className={styles.info}>
                <div className={styles.name}>
                  {league.name}
                  {(league.country || league.season) && (
                    <span className={styles.season}>{[league.country, league.season].filter(Boolean).join(' · ')}</span>
                  )}
                </div>
                <div className={styles.status}>
                  {renderStatusTag(state)}
                  {renderDetails(state)}
                </div>
              </div>
              <button
                type="button"
                className={`ls-btn ls-btn--outlined ${styles.button}`}
                disabled={isActive || isStarting}
                onClick={() => startMutation.mutate(league.id)}
              >
                {t('admin:leagueFullSyncStart')}
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

export default LeagueFullSyncPanel;
